/**
 * Login Page
 * POV-0013: Frontend login + session handling
 * 
 * Branding: HADEN ATLAS
 * - Background: Gradient #56C1FF → #FFFFFF
 * - HADEN: Racing Sans One, #101957
 * - ATLAS: Abril Fatface, #C8B59A
 * - Container: Frosted glass effect
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, LogIn, AlertCircle, Eye, EyeOff } from 'lucide-react';
import HadenAtlasLogo from '../components/HadenAtlasLogo';

const REMEMBERED_EMAIL_KEY = 'remembered_email';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect path from location state or default to home
  const from = location.state?.from?.pathname || '/';

  // Load remembered email on mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY);
    if (rememberedEmail) {
      setUsername(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await login(username, password, rememberMe);
      
      if (rememberMe) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, username);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }
      
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'linear-gradient(180deg, #56C1FF 0%, #FFFFFF 100%)'
      }}
    >
      {/* Frosted Glass Container - Compact */}
      <div 
        className="w-full max-w-sm rounded-2xl p-6 shadow-2xl"
        style={{
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
        }}
      >
        {/* Logo Section - No subtitle */}
        <div className="text-center mb-6">
          <div className="flex justify-center">
            <HadenAtlasLogo 
              size="large" 
              variant="dark"
              showSubtitle={false}
            />
          </div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive" className="bg-red-500/20 border-red-400/50 text-red-800">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="username" className="text-[#101957] font-medium">
              Email
            </Label>
            <Input
              id="username"
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your email"
              required
              disabled={isSubmitting}
              className="bg-white/70 border-white/50 text-[#101957] placeholder:text-slate-400 focus:border-[#56C1FF] focus:ring-[#56C1FF]/30"
              data-testid="login-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-[#101957] font-medium">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={isSubmitting}
                className="bg-white/70 border-white/50 text-[#101957] placeholder:text-slate-400 focus:border-[#56C1FF] focus:ring-[#56C1FF]/30 pr-10"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#101957] transition-colors focus:outline-none"
                data-testid="toggle-password-visibility"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="remember-me"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isSubmitting}
              className="h-4 w-4 rounded border-slate-300 bg-white/70 text-[#101957] focus:ring-[#56C1FF] cursor-pointer"
              data-testid="remember-me-checkbox"
            />
            <Label 
              htmlFor="remember-me" 
              className="text-sm text-[#101957]/70 cursor-pointer select-none"
            >
              Remember me for 30 days
            </Label>
          </div>

          <Button
            type="submit"
            className="w-full bg-[#101957] hover:bg-[#101957]/90 text-white font-medium py-2.5 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            disabled={isSubmitting}
            data-testid="login-submit-button"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                <LogIn className="mr-2 h-4 w-4" />
                Sign In
              </>
            )}
          </Button>
        </form>

        <div className="mt-5 text-center text-xs text-[#101957]/60">
          <p>AS9100D Compliant Operations Management System</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
